// /orgs/$orgSlug/projects/$projectId/dashboard (Stream V1).
//
// Route shell for the per-project progress dashboard. Chart visibility
// is URL state (`?hide=<chartId>`) so a filtered dashboard is
// shareable and survives back/forward - the same discipline the shot
// filters use. Project access is enforced server-side; 403 / 404 are
// surfaced here rather than falling through to the default boundary.

import {
  createFileRoute,
  Link,
  useNavigate,
} from '@tanstack/react-router';

import { ApiError, apiErrorMessage } from '../api/client';
import { DashboardPage } from '../components/DashboardPage';
import { DASHBOARD_CHARTS, type ChartId } from '../components/dashboardCharts';

interface DashboardSearch {
  hide?: ChartId[];
}

const VALID_CHART_IDS = new Set<string>(DASHBOARD_CHARTS.map((c) => c.id));

function validateDashboardSearch(
  raw: Record<string, unknown>
): DashboardSearch {
  const out: DashboardSearch = {};
  const rawHide = raw['hide'];
  const list = Array.isArray(rawHide)
    ? rawHide
    : typeof rawHide === 'string'
      ? [rawHide]
      : [];
  const clean = list.filter(
    (x): x is ChartId => typeof x === 'string' && VALID_CHART_IDS.has(x)
  );
  if (clean.length > 0) out.hide = clean;
  return out;
}

export const Route = createFileRoute(
  '/orgs/$orgSlug/projects/$projectId/dashboard'
)({
  component: DashboardRoute,
  errorComponent: DashboardRouteError,
  validateSearch: validateDashboardSearch,
});

function DashboardRoute(): React.JSX.Element {
  const { orgSlug, projectId } = Route.useParams();
  const { hide } = Route.useSearch();
  const navigate = useNavigate();

  function handleToggle(id: ChartId): void {
    const next = new Set<ChartId>(hide ?? []);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    const hidden = [...next];
    void navigate({
      to: '/orgs/$orgSlug/projects/$projectId/dashboard',
      params: { orgSlug, projectId },
      search: hidden.length > 0 ? { hide: hidden } : {},
    });
  }

  return (
    <DashboardPage
      orgSlug={orgSlug}
      projectId={projectId}
      hidden={hide ?? []}
      onToggleChart={handleToggle}
    />
  );
}

function DashboardRouteError({
  error,
}: {
  error: unknown;
}): React.JSX.Element {
  const isApi = error instanceof ApiError;
  const status = isApi ? error.status : null;
  const heading =
    status === 403
      ? 'You don’t have access to this project'
      : status === 404
        ? 'Project not found'
        : 'Something went wrong loading the dashboard';
  const body = apiErrorMessage(
    error,
    'Try again, or pick a different project from the organization page.'
  );

  return (
    <div className="mx-auto max-w-2xl py-7">
      <p className="text-text-faint text-11 mb-2 uppercase tracking-wider">
        Error
      </p>
      <h1 className="text-text text-28 mb-3 font-semibold">{heading}</h1>
      <p className="text-text-muted mb-5 text-13">{body}</p>
      <Link
        to="/"
        className="bg-accent text-accent-fg border-border-strong rounded-2 hover:bg-accent-hi inline-block border px-3 py-2 text-13 transition-colors"
      >
        Back to home
      </Link>
    </div>
  );
}
